// teamRating.js — team rating system shared by All4Indoor home, Rankings page,
// and team profile modal. Single source of truth for the scoring formula so
// the numbers match wherever they appear.
//
// 2026-05-28 — first cut, scoring values per Jake's spec:
//   Result    : W=+10, L=-5, D=+2, Forfeit=-10 (to the forfeiting team)
//   Cricket   : +1 per 10 runs scored,  -1 per 20 runs conceded
//   Netball   : +2 per goal scored,     -1 per goal conceded
//   Soccer    : +10 per goal scored,    -5 per goal conceded
//   Volleyball: +1 per 5 points scored, -1 per 10 points conceded
//
// Returns whole numbers (floored) for the per-sport contribution so a team
// doesn't get half-points for "almost reaching 10 runs". Result points are
// always whole. Forfeit-receiving team is treated as a normal win.

export const SCORING_RULES = {
  result: { win: 10, loss: -5, draw: 2, forfeit: -10 },
  cricket:    { forDivisor: 10, againstDivisor: 20, forSign:  1, againstSign: -1 },
  netball:    { forMultiplier:  2, againstMultiplier:  1, forSign:  1, againstSign: -1 },
  soccer:     { forMultiplier: 10, againstMultiplier:  5, forSign:  1, againstSign: -1 },
  volleyball: { forDivisor:  5, againstDivisor: 10, forSign:  1, againstSign: -1 },
};

// Sport-aware scoring contribution.
function scoringPointsFor(sport, forScore, againstScore) {
  const f = Math.max(0, Number(forScore) || 0);
  const a = Math.max(0, Number(againstScore) || 0);
  switch (sport) {
    case 'cricket':
      return Math.floor(f / 10) - Math.floor(a / 20);
    case 'netball':
      return (f * 2) - a;
    case 'soccer':
      return (f * 10) - (a * 5);
    case 'volleyball':
      return Math.floor(f / 5) - Math.floor(a / 10);
    default:
      // Unknown sport — only count result points
      return 0;
  }
}

// Returns { result, scoring, total, outcome } for one match from ONE team's
// perspective. Caller passes:
//   match — { home_team_id, away_team_id, home_score, away_score, status, forfeit_type }
//   teamId — UUID
//   sport — slug ('cricket', 'netball', 'soccer', 'volleyball')
export function computeMatchPoints(match, teamId, sport) {
  if (!match || !teamId) return null;
  const onHome = match.home_team_id === teamId;
  const onAway = match.away_team_id === teamId;
  if (!onHome && !onAway) return null;

  const status = (match.status || '').toLowerCase();
  const forfeitType = (match.forfeit_type || '').toLowerCase();
  const isForfeitMatch = forfeitType && forfeitType !== 'none';

  // Forfeit handling — the side listed in forfeit_type forfeited; other side
  // gets a clean win (no scoring points since scores often aren't recorded).
  if (status === 'forfeited' || isForfeitMatch) {
    const weForfeited = (forfeitType === 'home' && onHome) || (forfeitType === 'away' && onAway);
    if (weForfeited) {
      return { result: SCORING_RULES.result.forfeit, scoring: 0, total: SCORING_RULES.result.forfeit, outcome: 'F' };
    }
    return { result: SCORING_RULES.result.win, scoring: 0, total: SCORING_RULES.result.win, outcome: 'W' };
  }

  // Only count completed matches with both scores set
  if (status !== 'completed') return null;
  if (match.home_score == null || match.away_score == null) return null;

  const usScore  = onHome ? match.home_score : match.away_score;
  const oppScore = onHome ? match.away_score : match.home_score;

  let outcome, resultPoints;
  if (usScore > oppScore)      { outcome = 'W'; resultPoints = SCORING_RULES.result.win; }
  else if (usScore < oppScore) { outcome = 'L'; resultPoints = SCORING_RULES.result.loss; }
  else                          { outcome = 'D'; resultPoints = SCORING_RULES.result.draw; }

  const scoringPoints = scoringPointsFor(sport, usScore, oppScore);
  return {
    result: resultPoints,
    scoring: scoringPoints,
    total: resultPoints + scoringPoints,
    outcome,
  };
}

// Aggregate a team's rating across a list of matches. `seasonKeyOf(match)`
// lets the caller bucket matches into seasons (we use the season name from
// the competition where possible, falling back to calendar year).
//
// Returns:
//   { rating, played, wins, losses, draws, forfeits, pointsFor, pointsAgainst,
//     resultPoints, scoringPoints,
//     bySeason:    { [seasonKey]: { rating, played, wins, losses, draws, ... } },
//     bestSeason:  { season, rating, played } | null,
//     worstSeason: { season, rating, played } | null }
//
// We require a minimum of 3 played matches before a season qualifies for
// best/worst tracking, otherwise a single-match team gets a phantom "best
// season" of 1 win.
const MIN_SEASON_MATCHES = 3;

export function computeTeamRating(matches, teamId, sport, seasonKeyOf) {
  const agg = makeEmpty();
  const bySeason = {};

  for (const m of (matches || [])) {
    const pts = computeMatchPoints(m, teamId, sport);
    if (!pts) continue;
    accumulate(agg, m, teamId, pts);

    const sk = (seasonKeyOf ? seasonKeyOf(m) : null) || defaultSeasonKey(m);
    if (!bySeason[sk]) bySeason[sk] = makeEmpty();
    accumulate(bySeason[sk], m, teamId, pts);
  }

  // Best/worst season — only seasons with ≥ MIN_SEASON_MATCHES qualify
  const eligible = Object.entries(bySeason).filter(([_, s]) => s.played >= MIN_SEASON_MATCHES);
  let bestSeason = null;
  let worstSeason = null;
  if (eligible.length > 0) {
    eligible.sort((a, b) => b[1].rating - a[1].rating);
    const [bk, bv] = eligible[0];
    const [wk, wv] = eligible[eligible.length - 1];
    bestSeason  = { season: bk, rating: bv.rating, played: bv.played, wins: bv.wins, losses: bv.losses };
    worstSeason = { season: wk, rating: wv.rating, played: wv.played, wins: wv.wins, losses: wv.losses };
  }

  return { ...agg, bySeason, bestSeason, worstSeason };
}

function makeEmpty() {
  return {
    rating: 0, played: 0, wins: 0, losses: 0, draws: 0, forfeits: 0,
    pointsFor: 0, pointsAgainst: 0,
    resultPoints: 0, scoringPoints: 0,
  };
}

function accumulate(agg, match, teamId, pts) {
  agg.rating         += pts.total;
  agg.resultPoints   += pts.result;
  agg.scoringPoints  += pts.scoring;
  agg.played         += 1;
  if (pts.outcome === 'W') agg.wins      += 1;
  if (pts.outcome === 'L') agg.losses    += 1;
  if (pts.outcome === 'D') agg.draws     += 1;
  if (pts.outcome === 'F') agg.forfeits  += 1;
  if (match.home_score != null && match.away_score != null) {
    const onHome = match.home_team_id === teamId;
    agg.pointsFor     += onHome ? (match.home_score || 0) : (match.away_score || 0);
    agg.pointsAgainst += onHome ? (match.away_score || 0) : (match.home_score || 0);
  }
}

function defaultSeasonKey(match) {
  if (match.season_name) return match.season_name;
  if (match.scheduled_at) return String(new Date(match.scheduled_at).getFullYear());
  return 'Unknown';
}

// Convenience: rank a list of { teamId, name, sport, rating } objects.
// Mutates in place AND returns it for chaining.
export function rankTeams(rows) {
  rows.sort((a, b) => (b.rating || 0) - (a.rating || 0));
  return rows;
}
